import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MonoLabel } from '../components/Editorial';
import AdminPage from '../components/AdminShell';
import '../components/lists/Lists.css';
import './CampCheckInPage.css';
import SchoolPicker from '../components/SchoolPicker';
import Button from '../components/Button';

const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

export default function CampCheckInPage() {
  const { campId } = useParams();
  return <CheckInStation campId={campId} />;
}

function CheckInStation({ campId }) {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]); // upcoming camps for the dropdown
  const [camp, setCamp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [regs, setRegs] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState(null); // { text, type }
  const [pill, setPill] = useState('waiting'); // waiting | in | all
  const EMPTY_WALKIN = { first_name: '', last_name: '', email: '', graduation_year: '', high_school: '', uid: '' };
  const [walkIn, setWalkIn] = useState(null); // null = hidden; object = form shown
  const showMessage = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), type === 'error' ? 4000 : 2500);
  };
  const searchRef = useRef(null);
  const walkInSubmittingRef = useRef(false);

  // Upcoming camps for the header dropdown (loaded once).
  useEffect(() => {
    axios.get(`${API_URL}/camps`, cfg())
      .then((res) => {
        const start = todayStart();
        const upcoming = (res.data || [])
          .filter((c) => c.date && new Date(c.date) >= start)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setCamps(upcoming);
      })
      .catch(() => setCamps([]));
  }, [API_URL]);

  // Load the selected camp + its registrations; reset per-camp state on change.
  useEffect(() => {
    setCamp(null); setNotFound(false); setRegs([]);
    setQuery(''); setSelected(null); setWalkIn(null);
    if (!campId) return;
    let cancelled = false;
    axios.get(`${API_URL}/camps/${campId}`, cfg())
      .then((res) => { if (!cancelled) setCamp(res.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    axios.get(`${API_URL}/camps/${campId}/registrations`, cfg())
      .then((res) => { if (!cancelled) setRegs(res.data || []); })
      .catch(() => { if (!cancelled) setRegs([]); });
    return () => { cancelled = true; };
  }, [API_URL, campId]);

  // Focus the search box once a camp's data is loaded.
  useEffect(() => { if (camp) searchRef.current?.focus(); }, [camp]);

  const checkedIn = regs.filter((r) => r.attended_at).length;
  const q = query.trim().toLowerCase();
  const results = q === '' ? [] : regs.filter((r) =>
    (r.user_name || '').toLowerCase().includes(q) || (r.uid || '').toLowerCase().includes(q)
  ).slice(0, 8);

  const resolveScan = async (raw) => {
    const uid = raw.trim();
    if (!uid) return;
    const exact = regs.find((r) => r.uid && r.uid === uid);
    if (exact) { setSelected(exact); return; }
    // Any all-digit input goes to the server, which is the only thing that can
    // normalize the SHORT band form. Bands print `042`, not the 8-digit UID,
    // so `\d{6,9}` never let a typed band number reach the resolver at all.
    // Short 1-4 digit inputs are normalized to 8-digit band numbers (e.g., 7 → 90000007).
    const numeric = /^\d+$/.test(uid);
    if (!/^\d{1,9}$/.test(uid)) {
      // Name search keeps its single-result convenience. Numeric input never
      // gets it: `results` matches a UID by SUBSTRING, so on a 300-athlete
      // roster an 8-digit UID merely CONTAINING `042` can be the lone match
      // and would silently load a completely unrelated athlete.
      if (!numeric && results.length === 1) setSelected(results[0]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/athletes/lookup/${uid}`, cfg());
      if (res.data?.blank_walkin_band) {
        setWalkIn({ ...EMPTY_WALKIN, uid });           // band-locked new walk-in
        return;
      }
      // Known/aliased account: bind to this camp + check in via the band endpoint.
      const claim = await axios.post(`${API_URL}/camps/${campId}/walk-in-band`, {
        uid, first_name: res.data.first_name || '', last_name: res.data.last_name || '',
        email: res.data.email || '',
      }, cfg());
      setRegs((prev) => [claim.data, ...prev.filter((r) => r.id !== claim.data.id)]);
      setQuery(''); setSelected(claim.data);
      showMessage(`${claim.data.user_name} checked in`, 'success');
    } catch (e) {
      if (e.response?.status === 404) showMessage('Unknown band — re-scan or check the number', 'error');
      else showMessage('Lookup failed', 'error');
    }
  };

  const onSearchKeyDown = (e) => { if (e.key === 'Enter') resolveScan(query); };

  const onCheckedIn = (updated) => {
    const wasAttended = !!(regs.find((r) => r.id === updated.id)?.attended_at);
    setRegs((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(null);
    setQuery('');
    searchRef.current?.focus();
    showMessage(`${updated.user_name} ${wasAttended ? 'updated' : 'checked in'}`, 'success');
  };

  const onUpdateReg = (updated) => {
    setRegs((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
  };

  const submitWalkIn = async () => {
    if (walkInSubmittingRef.current) return;
    if (!walkIn.first_name.trim() || !walkIn.last_name.trim() || !walkIn.email.trim()) {
      showMessage('First name, last name, and email are required', 'error');
      return;
    }
    walkInSubmittingRef.current = true;
    const gradYear = walkIn.graduation_year === '' ? null : Number(walkIn.graduation_year);
    const highSchool = walkIn.high_school.trim() || null;
    const bandUid = (walkIn.uid || '').trim();
    try {
      let res;
      if (bandUid) {
        res = await axios.post(`${API_URL}/camps/${campId}/walk-in-band`, {
          uid: bandUid,
          first_name: walkIn.first_name.trim(),
          last_name: walkIn.last_name.trim(),
          email: walkIn.email.trim(),
          graduation_year: gradYear,
          high_school: highSchool,
        }, cfg());
      } else {
        res = await axios.post(`${API_URL}/camps/${campId}/walk-in`, {
          first_name: walkIn.first_name.trim(),
          last_name: walkIn.last_name.trim(),
          email: walkIn.email.trim(),
          graduation_year: gradYear,
          high_school: highSchool,
        }, cfg());
      }
      setRegs((prev) => [res.data, ...prev.filter((r) => r.id !== res.data.id)]);
      setWalkIn(null); setQuery(''); setSelected(res.data);
      showMessage(`${res.data.user_name} ${bandUid ? 'checked in' : 'added as walk-in'}`, 'success');
    } catch (e) {
      const d = e.response?.data?.detail;
      showMessage((d && d.message) || d || 'Walk-in failed', 'error');
    } finally {
      walkInSubmittingRef.current = false;
    }
  };

  const waiting = regs.filter((r) => !r.attended_at);
  const checked = regs.filter((r) => r.attended_at);
  const pool = pill === 'waiting' ? waiting : pill === 'in' ? checked : regs;
  const rows = q === '' ? pool : pool.filter((r) =>
    (r.user_name || '').toLowerCase().includes(q) || (r.uid || '').toLowerCase().includes(q));
  const pct = regs.length > 0 ? Math.round((checkedIn / regs.length) * 100) : 0;

  const quickCheckIn = async (r) => {
    try {
      const res = await axios.post(`${API_URL}/camps/registrations/${r.id}/check-in`, { attended: true }, cfg());
      onCheckedIn({ ...r, attended_at: res.data.attended_at });
    } catch (e) {
      const d = e.response?.data?.detail;
      showMessage((d && d.message) || d || 'Check-in failed', 'error');
    }
  };

  const crumbs = [{ label: 'Camps', to: '/camps' }, ...(camp ? [{ label: camp.name }] : []), { label: 'Check-in' }];

  const headerActions = (
    <div className="ci-head">
      {campId && camp && (
        <div className="ci-progress">
          <span className="ci-progress__text">{checkedIn} / {regs.length} checked in</span>
          <progress className="ci-progress__bar" max={regs.length || 1} value={checkedIn} aria-label={`${pct}% checked in`} />
        </div>
      )}
      <select className="adm-select ci-camp" value={campId || ''} aria-label="Select camp"
        onChange={(e) => navigate(e.target.value ? `/camps/checkin/${e.target.value}` : '/camps/checkin')}>
        <option value="">Select a camp…</option>
        {camps.map((c) => (
          <option key={c.id} value={c.id}>{c.name}{c.date ? ` — ${new Date(c.date).toLocaleDateString()}` : ''}</option>
        ))}
      </select>
    </div>
  );

  const pills = [['waiting', 'Waiting', waiting.length], ['in', 'Checked in', checked.length], ['all', 'All', regs.length]];

  return (
    <AdminPage active="camps" title="Check-in" breadcrumb={crumbs} actions={headerActions}>
      {!campId ? (
        <div className="adm__card"><p className="adm-empty">
          <strong>Select a camp</strong><br />
          {camps.length === 0 ? 'There are no upcoming camps.' : 'Choose a camp above to begin checking athletes in.'}
        </p></div>
      ) : notFound ? (
        <div className="adm__card"><p className="adm-error" role="alert">Camp not found. Pick a camp from the dropdown above.</p></div>
      ) : (
        <div className="ci-grid">
          <section className="adm__card ci-main" aria-label="Roster">
            {msg && <div className={`ls-banner ${msg.type === 'error' ? 'ls-banner--error' : ''}`} role="status">{msg.text}</div>}
            <div className="ci-search">
              <input ref={searchRef} type="text" className="ci-search__input" value={query}
                onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                onKeyDown={onSearchKeyDown}
                placeholder="Type a name or UID"
                aria-label="Search by name or UID" />
              <button type="button" className="ls-btn ls-btn--dark"
                onClick={() => { setSelected(null); setWalkIn({ ...EMPTY_WALKIN }); }}>+ Walk-in</button>
            </div>
            {walkIn && (
              <div className="ci-walkin">
                <WalkInForm walkIn={walkIn} setWalkIn={setWalkIn} onSubmit={submitWalkIn} onCancel={() => setWalkIn(null)} />
              </div>
            )}
            <div className="ci-pills" role="group" aria-label="Roster filter">
              {pills.map(([key, label, n]) => (
                <button key={key} type="button" className={`ci-pill ${pill === key ? 'ci-pill--on' : ''}`}
                  aria-pressed={pill === key} onClick={() => setPill(key)}>{`${label} ${n}`}</button>
              ))}
            </div>
            {rows.length === 0 ? (
              q !== '' ? (
                <div className="ci-empty">
                  <p>No one matching "{q === query.trim().toLowerCase() ? query.trim() : q}" is registered for {camp ? camp.name : 'this camp'}.</p>
                  <button type="button" className="ls-btn ls-btn--primary" onClick={() => setWalkIn({ ...EMPTY_WALKIN })}>+ Create walk-in</button>
                </div>
              ) : <p className="adm-empty">{regs.length === 0 ? 'No one is registered for this camp yet.' : 'Nobody in this list.'}</p>
            ) : (
              <ul className="ci-rows">
                {rows.map((r) => (
                  <li key={r.id} className={`ci-row ${selected?.id === r.id ? 'ci-row--on' : ''}`}>
                    <button type="button" className="ci-row__main" onClick={() => setSelected(r)}>
                      <span className="ci-row__jersey" aria-hidden="true">{r.jersey_number != null ? `#${r.jersey_number}` : '—'}</span>
                      <span className="ci-row__text">
                        <span className="ci-row__name">{r.user_name}</span>
                        <span className="ci-row__sub">
                          {[r.position, r.graduation_year && `'${String(r.graduation_year).slice(-2)}`, r.high_school].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </button>
                    {r.attended_at
                      ? <span className="adm-chip adm-chip--green">✓ In</span>
                      : <button type="button" className="ls-btn ls-btn--primary ls-btn--tiny" aria-label={`Check in ${r.user_name}`}
                          onClick={() => quickCheckIn(r)}>Check in</button>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <aside className="adm__card ci-side" aria-label="Selected athlete">
            <div className="srch-label">SELECTED</div>
            {selected ? (
              <AthleteCard reg={selected} apiUrl={API_URL} campId={campId} allRegs={regs}
                onBack={() => setSelected(null)} onCheckedIn={onCheckedIn}
                onUpdateReg={onUpdateReg} showMessage={showMessage} />
            ) : (
              <p className="adm-empty">Pick an athlete, or scan a band, to check them in.</p>
            )}
          </aside>
        </div>
      )}
    </AdminPage>
  );
}

function WalkInForm({ walkIn, setWalkIn, onSubmit, onCancel }) {
  const set = (field, val) => setWalkIn((w) => ({ ...w, [field]: val }));
  const onEnter = (e) => { if (e.key === 'Enter') onSubmit(); };
  const field = (name, label, type, width) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>{label}</label>
      <input type={type} value={walkIn[name]}
        onChange={(e) => set(name, e.target.value)} onKeyDown={onEnter}
        aria-label={label} style={{ padding: '8px', width }} />
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Line 1: identity + band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
        {field('first_name', 'First name *', 'text', 130)}
        {field('last_name', 'Last name *', 'text', 130)}
        {field('email', 'Email *', 'email', 200)}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Wristband UID</label>
          <input type="text" inputMode="numeric" value={walkIn.uid}
            onChange={(e) => set('uid', e.target.value)} onKeyDown={onEnter}
            placeholder="Type the number on the band"
            aria-label="Wristband UID (type the number printed on the band)"
            style={{ padding: '8px', width: 210 }} />
        </div>
      </div>
      {/* Line 2: grad year + high school */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
        {field('graduation_year', 'Grad year', 'number', 100)}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 280 }}>
          <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>High school</label>
          <SchoolPicker value={walkIn.high_school}
            onChange={(s) => set('high_school', s.name)} />
        </div>
      </div>
      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" onClick={onSubmit}>Add walk-in</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function AthleteCard({ reg, apiUrl, campId, allRegs, onBack, onCheckedIn, onUpdateReg, showMessage }) {
  const [jersey, setJersey] = useState(reg.jersey_number ?? '');
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [editingEmails, setEditingEmails] = useState(false);
  const [athleteEmail, setAthleteEmail] = useState(reg.user_email || '');
  const [guardianEmail, setGuardianEmail] = useState(reg.guardian_email || '');
  const [savingEmails, setSavingEmails] = useState(false);

  useEffect(() => {
    setEditingEmails(false);
    setAthleteEmail(reg.user_email || '');
    setGuardianEmail(reg.guardian_email || '');
  }, [reg.id, reg.user_email, reg.guardian_email]);

  const saveEmails = async () => {
    const aNew = athleteEmail.trim();
    const gNew = guardianEmail.trim();
    if (!aNew) { showMessage('Athlete email cannot be empty', 'error'); return; }
    const payload = {};
    if (aNew.toLowerCase() !== (reg.user_email || '').toLowerCase()) payload.athlete_email = aNew;
    if (gNew.toLowerCase() !== (reg.guardian_email || '').toLowerCase()) payload.guardian_email = gNew;
    if (Object.keys(payload).length === 0) { setEditingEmails(false); return; }
    setSavingEmails(true);
    try {
      const res = await axios.patch(`${apiUrl}/athletes/${reg.user_id}/emails`, payload, cfg());
      onUpdateReg({ ...reg, user_email: res.data.athlete_email,
        guardian_email: res.data.guardian_email, guardian_user_id: res.data.guardian_user_id });
      setEditingEmails(false);
      showMessage('Emails updated', 'success');
    } catch (e) {
      const d = e.response?.data?.detail;
      showMessage((d && d.message) || d || 'Email update failed', 'error');
    } finally {
      setSavingEmails(false);
    }
  };

  useEffect(() => {
    setJersey(reg.jersey_number ?? '');
    setPhoto(null);
    let cancelled = false;
    if (reg.uid) {
      axios.get(`${apiUrl}/athletes/lookup/${reg.uid}`, cfg())
        .then((res) => { if (!cancelled) setPhoto(res.data?.profile_photo || null); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [reg.id, reg.uid, apiUrl]);

  const attendedTime = reg.attended_at
    ? new Date(reg.attended_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;

  const replaceBand = async () => {
    const band = window.prompt('Scan or type the NEW blank band UID to assign to ' + reg.user_name);
    if (!band || !band.trim()) return;
    try {
      const res = await axios.post(`${apiUrl}/camps/${campId}/check-in/band`,
        { athlete_user_id: reg.user_id, band_uid: band.trim() }, cfg());
      showMessage(`Band ${res.data.band_number} now scans to ${reg.user_name}`, 'success');
    } catch (e) {
      const d = e.response?.data?.detail;
      showMessage((d && d.message) || d || 'Replace band failed', 'error');
    }
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const trimmed = String(jersey).trim();
      const jn = trimmed === '' ? null : Number(trimmed);
      let savedJersey = reg.jersey_number ?? null;
      if (jn !== (reg.jersey_number ?? null)) {
        if (jn !== null && (!Number.isInteger(jn) || jn < 0 || jn > 999)) {
          showMessage('Jersey number must be 0–999', 'error');
          return;
        }
        let force = false;
        if (jn !== null) {
          const clash = allRegs.find((r) => r.id !== reg.id && r.jersey_number === jn);
          if (clash && !window.confirm(`#${jn} is already assigned to ${clash.user_name}. Assign anyway?`)) {
            return;
          }
          force = !!clash;
        }
        await axios.patch(`${apiUrl}/camps/registrations/${reg.id}/jersey`, { jersey_number: jn, force }, cfg());
        savedJersey = jn;
      }
      const res = await axios.post(`${apiUrl}/camps/registrations/${reg.id}/check-in`, { attended: true }, cfg());
      onCheckedIn({ ...reg, attended_at: res.data.attended_at, jersey_number: savedJersey });
    } catch (e) {
      const d = e.response?.data?.detail;
      showMessage((d && d.message) || d || 'Check-in failed', 'error');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 96, height: 96, borderRadius: 8, overflow: 'hidden', background: '#e2e8f0', flexShrink: 0 }}>
        {photo ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0 }}>{reg.user_name}</h3>
        <MonoLabel>UID {reg.uid || '—'}{reg.graduation_year ? ` · CLASS OF ${reg.graduation_year}` : ''}</MonoLabel>
        {reg.high_school && <div className="ed-list__row-sub">{reg.high_school}</div>}
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4, fontSize: 13, alignItems: 'center' }}>
          <span style={{ color: '#64748b' }}>Athlete email</span>
          {editingEmails ? (
            <input type="email" value={athleteEmail} onChange={(e) => setAthleteEmail(e.target.value)}
              aria-label="Athlete email" style={{ padding: '6px', width: '100%', maxWidth: 320 }} />
          ) : (<span>{reg.user_email || '—'}</span>)}

          <span style={{ color: '#64748b' }}>Guardian email</span>
          {editingEmails ? (
            <input type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)}
              placeholder="(none)" aria-label="Guardian email" style={{ padding: '6px', width: '100%', maxWidth: 320 }} />
          ) : (
            <span>{reg.guardian_email || '—'}{reg.guardian_name ? `  ·  ${reg.guardian_name}` : ''}</span>
          )}

          {[
            ['Cell', reg.phone],
            // Two different facts, labelled so a volunteer cannot confuse them:
            // what they registered AND PAID for, versus what their profile says.
            // The ATH election rides on the registered position rather than
            // getting its own row: a volunteer reading "OT · ATH (both ways)"
            // cannot mistake it for a second, separate position.
            ['Registered as', reg.position_slug
              ? reg.position_slug.toUpperCase() + (reg.two_way_athlete ? ' · ATH (both ways)' : '')
              : (reg.two_way_athlete ? 'ATH (both ways)' : null)],
            ['Profile position', reg.position],
            ['Jersey size', reg.tshirt_size],
            ['Source', reg.registration_source === 'walk_in' ? 'Walk-in' : 'Registered'],
          ].filter(([, v]) => v).map(([label, value]) => (
            <React.Fragment key={label}>
              <span style={{ color: '#64748b' }}>{label}</span>
              <span>{value}</span>
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 6 }}>
          {editingEmails ? (
            <span style={{ display: 'flex', gap: 8 }}>
              <Button variant="primary" disabled={savingEmails} onClick={saveEmails}>Save emails</Button>
              <Button variant="secondary" onClick={() => { setEditingEmails(false); setAthleteEmail(reg.user_email || ''); setGuardianEmail(reg.guardian_email || ''); }}>Cancel</Button>
            </span>
          ) : (
            <Button variant="secondary" onClick={() => setEditingEmails(true)}>Edit emails</Button>
          )}
        </div>
        <div style={{ marginTop: 8, color: attendedTime ? '#16a34a' : '#64748b' }}>
          {attendedTime ? `✓ Checked in ${attendedTime}` : 'Not checked in'}
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Jersey</label>
            <input type="number" min={0} max={999} step={1} value={jersey}
              onChange={(e) => setJersey(e.target.value)}
              aria-label={`Jersey number for ${reg.user_name}`}
              style={{ width: 80, padding: '8px', textAlign: 'center' }} />
          </div>
          <Button variant="primary" disabled={saving} onClick={save}>
            {reg.attended_at ? 'Update' : 'Check In'}
          </Button>
          <Button variant="secondary" onClick={replaceBand}>Replace band</Button>
          <Button variant="secondary" onClick={onBack}>Back</Button>
        </div>
      </div>
    </div>
  );
}
